// Portfolio Administration v1.0 PR 3 §7.3 — one section in the Parties tab.

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { PartyRow } from "./PartyRow";
import { AddPartyModal } from "./AddPartyModal";
import type { LoanPartyAssignmentDto } from "@/api/portfolioAdmin/types";

interface PartyRosterSectionProps {
  loanId: string;
  title: string;
  parties: LoanPartyAssignmentDto[];
  defaultCounterpartyType?: string;
  defaultRoleOnLoan?: string;
  testId?: string;
  /** When true, the section is read-only (no Add button) — used for the
   *  Vendors / Local Enforcers / Phoenix internal team summaries that
   *  live on dedicated tabs. */
  readOnly?: boolean;
  /** Optional empty-state message override. */
  emptyMessage?: string;
}

export function PartyRosterSection({
  loanId,
  title,
  parties,
  defaultCounterpartyType,
  defaultRoleOnLoan,
  testId,
  readOnly = false,
  emptyMessage,
}: PartyRosterSectionProps) {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-2">
        <h3 className="font-display font-bold text-h4 text-onyx m-0" data-testid={testId}>
          {title}{" "}
          <span className="text-caption text-gray-mid normal-case font-normal">
            ({parties.length})
          </span>
        </h3>
        {!readOnly && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setAddOpen(true)}
            data-testid={`${testId}-add`}
          >
            <Plus size={14} aria-hidden /> Add party
          </Button>
        )}
      </div>
      {parties.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          {emptyMessage ?? "No parties assigned yet."}
        </p>
      ) : (
        <div className="flex flex-col gap-space-1">
          {parties.map((p) => (
            <PartyRow key={p.id} loanId={loanId} party={p} readOnly={readOnly} />
          ))}
        </div>
      )}
      {addOpen && (
        <AddPartyModal
          loanId={loanId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          defaultCounterpartyType={defaultCounterpartyType}
          defaultRoleOnLoan={defaultRoleOnLoan}
        />
      )}
    </Card>
  );
}
